/**
 * @file signupIntent.js
 * @description Tiny persistence helper for "why did this visitor sign up" intent. When a
 *   guest clicks a feature-specific CTA (e.g. the free Pay & Hours tracker), we stash an
 *   intent string that survives the auth + onboarding round-trip via localStorage, so App
 *   can route them straight to that feature once they're a fully onboarded therapist —
 *   honoring the thing that brought them in instead of dropping them on the generic map.
 * @module lib/signupIntent
 */
import { track, A } from "./analytics";

const KEY = "ac_intent";
/** An intent older than this is ignored, so a CTA clicked days ago doesn't steer an unrelated later signup. */
export const INTENT_TTL_MS = 72 * 60 * 60 * 1000;

// Stored as {"v": intent, "at": epoch ms}. A legacy plain-string value (written before the TTL) is still read.
function read() {
  const raw = localStorage.getItem(KEY);
  if (!raw) return null;
  let v = raw, at = null;
  try {
    const o = JSON.parse(raw);
    if (o && typeof o === "object" && typeof o.v === "string") { v = o.v; at = Number(o.at) || null; }
  } catch { /* legacy plain string */ }
  if (at !== null && Date.now() - at > INTENT_TTL_MS) { localStorage.removeItem(KEY); return null; }
  return v || null;
}

/** Record the intent that brought a guest to sign up (e.g. "tracker"). */
export function setSignupIntent(intent) {
  try { localStorage.setItem(KEY, JSON.stringify({ v: intent, at: Date.now() })); } catch { /* storage blocked */ }
}

/** Read the stored signup intent WITHOUT clearing it. Returns null if none (or expired). */
export function peekSignupIntent() {
  try { return read(); } catch { return null; }
}

/** Read and clear the stored signup intent (one-shot). Returns null if none (or expired). */
export function consumeSignupIntent() {
  try {
    const v = read();
    localStorage.removeItem(KEY);
    return v;
  } catch { return null; }
}

/** Forget any stored intent. */
export function clearSignupIntent() {
  try { localStorage.removeItem(KEY); } catch { /* storage blocked */ }
}

// ── The case a guest meant to act on (owner + David 2026-10-23: 218 case views, 0 applications) ──
// A signed-out visitor who taps Apply (or Save / Pass) on a case is sent to sign up. This remembers WHICH case,
// on the device, so it survives a reload, an app switch on a phone, email verification and onboarding. After the
// minimum profile, App.js reopens that case with the application form in view, then clears it. Same 72-hour window
// as the signup intent above.
const CASE_KEY = "ac_pending_case";

/** Remember the case a guest wants to apply to (action "apply") or save ("save"). */
export function setPendingCase(c, action = "apply") {
  if (!c || !c.id) return;
  try {
    localStorage.setItem(CASE_KEY, JSON.stringify({
      id: c.id, code: c.caseCode || c.case_code || null, type: c.type || null, city: c.city || null,
      action: action === "save" ? "save" : "apply", at: Date.now(),
    }));
  } catch { /* storage blocked */ }
}

/** The remembered case ({ id, code, type, city, action, at }) or null when none or older than 72 hours. */
export function peekPendingCase() {
  try {
    const o = JSON.parse(localStorage.getItem(CASE_KEY) || "null");
    if (!o || typeof o.id !== "string") return null;
    if (!Number.isFinite(o.at) || Date.now() - o.at > INTENT_TTL_MS) { localStorage.removeItem(CASE_KEY); return null; }
    return o;
  } catch { return null; }
}

/** Forget the remembered case. */
export function clearPendingCase() {
  try { localStorage.removeItem(CASE_KEY); } catch { /* storage blocked */ }
}

/** The provider role a case's signup should start on: BCBA and BCaBA cases preselect their role, everything else RBT/BT. */
export function roleForCaseType(type) {
  const t = String(type || "").toUpperCase();
  if (t === "BCBA") return "bcba";
  if (t === "BCABA") return "bcaba";
  return "rbt";
}

/**
 * A signed-out visitor acted on a case (Apply, unlock details, Save): remember it for after signup and record the tap
 * (A.GUEST_APPLY_CLICK, the first step of the per-case funnel in Admin > Analytics). The one entry point for every
 * guest button on a case.
 */
export function rememberCaseForSignup(c, action = "apply") {
  if (!c || !c.id) return;
  setPendingCase(c, action);
  track(A.GUEST_APPLY_CLICK, { supabaseMeta: { case_id: c.id, case_code: c.caseCode || c.case_code || null, action: action === "save" ? "save" : "apply" } });
}
